import React, { useState } from "react"

import FirmenGesundheitFormStyles from "./firmengesundheit.module.css"
import FlexWrapper from "../../../shared/components/fields/flex-wrapper"
import IconAction from "../../../shared/components/universal/icon-action"
import Input from "../../../shared/components/fields/input"
import Radio from "../../../shared/components/fields/radio"
import Spacer from "../universal/spacer"
import Text from "../matrix/text"
import classNames from "classnames"
import { errorToast } from "../../../shared/utils/error-toast"
import { genders } from "../../../shared/config/genders"
import { scrollTo } from "../../../shared/utils/smooth-scroll"
import { useForm } from "react-hook-form"
import { useMediaQuery } from "../../../hooks/use-media-query-admin"
import { usePostContact } from "../../../hooks/contact/use-query-contact"
import { useTranslation } from "react-i18next"
import { useUrlSearchParams } from "../../../shared/hooks/use-url-search-params"

const FirmenGesundheitForm = () => {
    const { t } = useTranslation("translations")
    const [isSuccess, setIsSuccess] = useState(false)

    const {
        handleSubmit,
        control,
        reset,
        register,
        formState: { errors },
    } = useForm({
        mode: "onTouched",
        defaultValues: {
            salutation: genders[0].id,
            company_name: "",
            firstname: "",
            lastname: "",
            street: "",
            postcode: "",
            city: "",
            phone: "",
            email: "",
            recognized: "",
        },
        context: undefined,
        criteriaMode: "firstError",
        shouldFocusError: true,
    })

    const [params] = useUrlSearchParams()
    const mutate = usePostContact()

    const submit = async values => {
        const body = {
            category_id: process.env.CONTACT_BGM_CATEGORY_ID,
            firstname: values.firstname,
            lastname: values.lastname,
            email: values.email,
            phone: values.phone,
            body: t("company_health_submit_body", {
                salutation: genders.find(g => g.id === values.salutation).visibleInAddress
                    ? t(genders.find(g => g.id === values.salutation).name)
                    : "",
                company_name: values.company_name,
                firstname: values.firstname,
                lastname: values.lastname,
                phone: values.phone,
                email: values.email,
                street: values.street,
                postcode: values.postcode,
                city: values.city,
                recognized: values.recognized,
            }),
        }

        if (params?.utm_source) {
            body.body += `\n\nutm_source: ${params.utm_source}\nutm_medium: ${params.utm_medium}\nutm_campaign: ${params.utm_campaign}`
        }

        try {
            await mutate.mutateAsync(body)
            reset()
            setIsSuccess(true)
            scrollTo("success_message")
        } catch (error) {
            errorToast(error)
        }
    }

    const isMobile = useMediaQuery("(max-width: 767px)", { initializeWithValue: false })

    return (
        <div className={FirmenGesundheitFormStyles.wrapper}>
            {!isSuccess ? (
                <div className={FirmenGesundheitFormStyles.form}>
                    <h3>{t("company_health_appointment_headline")}</h3>
                    <Spacer small />
                    <div>
                        <form onSubmit={handleSubmit(submit)} name="firmengesundheit">
                            <div>
                                <span className={FirmenGesundheitFormStyles.label}>
                                    <strong>{t("company_health_company")}</strong>
                                </span>
                                <Spacer small />
                                <Input
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: t("form_error_required"),
                                        },
                                    }}
                                    type="text"
                                    name="company_name"
                                    label={t("company_health_company_name")}
                                    required
                                />
                                <Input
                                    control={control}
                                    type="text"
                                    name="street"
                                    label={t("company_health_company_street")}
                                />
                                <FlexWrapper childWidths={[30, 70]}>
                                    <Input
                                        control={control}
                                        rules={{
                                            required: {
                                                value: true,
                                                message: t("form_error_required"),
                                            },
                                            pattern: {
                                                value: /\b[0-9]{4,5}\b/,
                                                message: t("company_health_postcode_pattern"),
                                            },
                                        }}
                                        type="text"
                                        name="postcode"
                                        label={t("company_health_postcode_label")}
                                        autocomplete="postal-code"
                                        length={5}
                                        required
                                    />
                                    <Input
                                        control={control}
                                        rules={{
                                            required: {
                                                value: true,
                                                message: t("form_error_required"),
                                            },
                                        }}
                                        type="text"
                                        name="city"
                                        label={t("company_health_city_label")}
                                        autocomplete="address-level2"
                                        required
                                    />
                                </FlexWrapper>
                            </div>
                            <Spacer medium />
                            <span
                                className={classNames(
                                    FirmenGesundheitFormStyles.label,
                                    FirmenGesundheitFormStyles.noMargin
                                )}
                            >
                                <strong>{t("company_health_contact_person")}</strong>
                            </span>
                            <Spacer small />
                            <Radio
                                register={register("salutation", {
                                    required: { value: true, message: t("form_error_required") },
                                })}
                                error={errors}
                                label={t("partner_form_salutation_label")}
                                flex
                                options={genders.map(g => ({
                                    label: t(g.name),
                                    value: g.id,
                                }))}
                                required
                            />
                            <FlexWrapper childWidths={isMobile ? [100, 100] : [50, 50]} wrap>
                                <Input
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: t("form_error_required"),
                                        },
                                    }}
                                    type="text"
                                    name="firstname"
                                    label={t("contact_form_firstname_label")}
                                    autocomplete="given-name"
                                    required
                                />
                                <Input
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: t("form_error_required"),
                                        },
                                    }}
                                    type="text"
                                    name="lastname"
                                    label={t("contact_form_lastname_label")}
                                    autocomplete="family-name"
                                    required
                                />
                            </FlexWrapper>
                            <div>
                                <Input
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: t("form_error_required"),
                                        },
                                    }}
                                    type="tel"
                                    name="phone"
                                    label={t("partner_form_phone_label")}
                                    required
                                />
                                <Input
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: t("form_error_required"),
                                        },
                                    }}
                                    type="email"
                                    name="email"
                                    label={t("contact_form_email_label")}
                                    required
                                />
                                <Input
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: t("form_error_required"),
                                        },
                                    }}
                                    type="text"
                                    name="recognized"
                                    label={t("company_health_recognized")}
                                    required
                                />
                                <div className={FirmenGesundheitFormStyles.smallprint}>
                                    <p>{t("company_health_legal_text")}</p>
                                </div>
                                <p className={FirmenGesundheitFormStyles.asterisk}>
                                    <span style={{ color: "#bf4751" }}>* </span>
                                    {t("form_required_field")}
                                </p>
                                <Spacer small />
                                <IconAction title={t("company_health_icon_action_send")} button submit />
                            </div>
                        </form>
                    </div>
                </div>
            ) : (
                <div className={FirmenGesundheitFormStyles.success} id="success_message">
                    <div className={FirmenGesundheitFormStyles.circle}>
                        <i className="lavita-icon"></i>
                    </div>
                    <Spacer small />
                    <Text
                        small
                        source={t("company_health_success")}
                        align={{ desktop: "center", tablet: "center", mobile: "center" }}
                    />
                </div>
            )}
        </div>
    )
}

export default FirmenGesundheitForm
